import React, { useState, useEffect } from 'react';
import { X, Download, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { Typography } from '../../interfaces/typography';
import { useSessionExport } from '../../lib/redux/sessions/lists/session-export-hook';
import { SessionHistoryTabs } from '../History/views';
import { BackendFilter, SessionListResponse } from '../../interfaces/sessions';
import {
  PROSPECT_INFO_COLUMNS,
  SESSION_METRICS_COLUMNS,
  CallMetricColumns,
  SessionV2Columns,
  TranscriptDataColumns
} from '../../interfaces/table';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  tab: SessionHistoryTabs;
  passed_filter?: BackendFilter;
}

export function ExportModal({ isOpen, onClose, tab, passed_filter }: ExportModalProps) {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');

  const { exportSessions, cancelExport, resetExport, progress, exportedSessions, columns } = useSessionExport({
    tab,
    passed_filter,
  });

  // Get users for name lookup
  const visibleAccounts = useSelector((state: RootState) => state.visibleAccounts.value);

  // Column selection state - all enabled by default except transcript
  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({
    timestamp: true,
    rep_name: true,
    prospect_name: true,
    prospect_title: true,
    company_name: true,
    cadence: true,
    prospect_list: true,
    from_number: true,
    to_number: true,
    duration: true,
    parallel_lines: true,
    disposition: true,
    sentiment: true,
    purpose: true,
    linkedin_url: true,
    transcript_link: true,
    transcript_text: false, // Start unchecked
  });

  // Check which optional columns are available
  const hasDisposition = columns.some(c => c.column_name === SESSION_METRICS_COLUMNS.USER_DISPOSITION);
  const hasSentiment = columns.some(c => c.column_name === SESSION_METRICS_COLUMNS.USER_SENTIMENT);
  const hasPurpose = columns.some(c => c.column_name === SESSION_METRICS_COLUMNS.USER_PURPOSE);

  // Initialize with today's date
  useEffect(() => {
    if (isOpen) {
      const today = new Date();

      setStartDate(formatDateForInput(today));
      setEndDate(formatDateForInput(today));
      setDateError('');
      resetExport();
    }
  }, [isOpen, resetExport]);

  // Add warning when export is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (progress.isExporting) {
        e.preventDefault();
        e.returnValue = 'Export in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    if (progress.isExporting) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [progress.isExporting]);

  const formatDateForInput = (date: Date): string => {
    // Use local timezone instead of UTC to avoid date shifting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateError('Please select both start and end dates');
      return false;
    }

    const startDateTime = new Date(start).getTime();
    const endDateTime = new Date(end).getTime();

    if (startDateTime > endDateTime) {
      setDateError('Start date must be before end date');
      return false;
    }

    const weekInMs = 7 * 24 * 60 * 60 * 1000;
    if (endDateTime - startDateTime > weekInMs) {
      setDateError('Date range cannot exceed 1 week');
      return false;
    }

    setDateError('');
    return true;
  };

  const handleExport = async () => {
    if (!validateDateRange(startDate, endDate)) {
      return;
    }

    // Parse dates in local timezone and set proper time boundaries
    // Start date should be at 00:00:00.000 local time
    const start = new Date(startDate + 'T00:00:00.000');

    // End date should be at 23:59:59.999 local time
    const end = new Date(endDate + 'T23:59:59.999');

    const sessions = await exportSessions(start, end);

    if (sessions.length > 0) {
      downloadCSV(sessions);
    }
  };

  const downloadCSV = (sessions: SessionListResponse[]) => {
    // Build CSV headers based on selected columns
    const headers: string[] = [];
    if (selectedColumns.timestamp) headers.push('Timestamp');
    if (selectedColumns.rep_name) headers.push('Rep Name');
    if (selectedColumns.prospect_name) headers.push('Prospect Name');
    if (selectedColumns.prospect_title) headers.push('Prospect Title');
    if (selectedColumns.company_name) headers.push('Company Name');
    if (selectedColumns.cadence) headers.push('Cadence');
    if (selectedColumns.prospect_list) headers.push('Prospect List');
    if (selectedColumns.from_number) headers.push('From Number');
    if (selectedColumns.to_number) headers.push('To Number');
    if (selectedColumns.duration) headers.push('Duration (seconds)');
    if (selectedColumns.parallel_lines) headers.push('Parallel Lines');
    if (selectedColumns.disposition && hasDisposition) headers.push('Disposition');
    if (selectedColumns.sentiment && hasSentiment) headers.push('Sentiment');
    if (selectedColumns.purpose && hasPurpose) headers.push('Purpose');
    if (selectedColumns.linkedin_url) headers.push('Prospect LinkedIn URL');
    if (selectedColumns.transcript_link) headers.push('Transcript Link');
    if (selectedColumns.transcript_text) headers.push('Transcript Text');

    // Build CSV rows
    const rows = sessions.map((session) => {
      // Get basic session info
      const startDate = session.result.get(SessionV2Columns.STARTED_AT);
      const timestamp = startDate instanceof Date ? startDate.toISOString() : '';

      // Get user/rep name
      const userId = session.result.get(SessionV2Columns.RESOURCE_ID);
      const repName = visibleAccounts?.users.find((u) => u.user_id === userId)?.user_name || 'Unknown';

      // Get prospect info
      const prospectName = session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME) || '';
      const prospectTitle = session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_TITLE) || '';
      const companyName = session.result.get(PROSPECT_INFO_COLUMNS.COMPANY_NAME) || '';
      const cadence = session.result.get(PROSPECT_INFO_COLUMNS.CADENCE) || '';
      const prospectList = session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_LIST) || '';
      const prospectLinkedInUrl = session.result.get(PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL) || '';

      // Get phone numbers
      const fromNumber = session.agent_address || '';
      const toNumber = session.customer_address || '';

      // Get call metrics
      const callDuration = session.result.get(CallMetricColumns.CALL_DURATION) || 0;
      const durationInSeconds = typeof callDuration === 'number' ? callDuration : parseFloat(callDuration as string);
      const parallelLines = session.result.get(CallMetricColumns.PARALLEL_LINES) || 1;

      // Get disposition/sentiment/purpose (only if they were fetched)
      const disposition = hasDisposition ? (session.result.get(SESSION_METRICS_COLUMNS.USER_DISPOSITION) || '') : '';
      const sentiment = hasSentiment ? (session.result.get(SESSION_METRICS_COLUMNS.USER_SENTIMENT) || '') : '';
      const purpose = hasPurpose ? (session.result.get(SESSION_METRICS_COLUMNS.USER_PURPOSE) || '') : '';

      // Generate transcript link
      const transcriptLink = `${window.location.origin}/transcripts?id=${session.session_id}`;

      // Get transcript text with timestamps (combines rep and prospect text in chronological order)
      const repText = session.result.get(TranscriptDataColumns.REP_TEXT) || '';
      const prospectText = session.result.get(TranscriptDataColumns.PROSPECT_TEXT) || '';
      const repStartsStr = session.result.get(TranscriptDataColumns.REP_STARTS_STR) || '';
      const prospectStartsStr = session.result.get(TranscriptDataColumns.PROSPECT_STARTS_STR) || '';

      let transcriptText = '';

      if (repText || prospectText) {
        // Parse transcript segments with timestamps
        const repSegments = repText && repStartsStr
          ? repText.split(' ').map((text, i) => ({
              text,
              timestamp: parseFloat(repStartsStr.split(' ')[i] || '0'),
              speaker: 'Rep'
            }))
          : [];

        const prospectSegments = prospectText && prospectStartsStr
          ? prospectText.split(' ').map((text, i) => ({
              text,
              timestamp: parseFloat(prospectStartsStr.split(' ')[i] || '0'),
              speaker: 'Prospect'
            }))
          : [];

        // Combine and sort by timestamp
        const allSegments = [...repSegments, ...prospectSegments].sort((a, b) => a.timestamp - b.timestamp);

        // Group consecutive segments by same speaker
        const groupedTranscript: { speaker: string; text: string }[] = [];
        allSegments.forEach(segment => {
          const lastGroup = groupedTranscript[groupedTranscript.length - 1];
          if (lastGroup && lastGroup.speaker === segment.speaker) {
            lastGroup.text += ' ' + segment.text;
          } else {
            groupedTranscript.push({ speaker: segment.speaker, text: segment.text });
          }
        });

        // Format as "Speaker: text"
        transcriptText = groupedTranscript
          .map(g => `${g.speaker}: ${g.text}`)
          .join(' | ');
      }

      // Build row based on selected columns (same order as headers)
      const row: string[] = [];
      if (selectedColumns.timestamp) row.push(timestamp);
      if (selectedColumns.rep_name) row.push(escapeCSV(repName));
      if (selectedColumns.prospect_name) row.push(escapeCSV(prospectName));
      if (selectedColumns.prospect_title) row.push(escapeCSV(prospectTitle));
      if (selectedColumns.company_name) row.push(escapeCSV(companyName));
      if (selectedColumns.cadence) row.push(escapeCSV(cadence));
      if (selectedColumns.prospect_list) row.push(escapeCSV(prospectList));
      if (selectedColumns.from_number) row.push(fromNumber);
      if (selectedColumns.to_number) row.push(toNumber);
      if (selectedColumns.duration) row.push(durationInSeconds.toString());
      if (selectedColumns.parallel_lines) row.push(parallelLines.toString());
      if (selectedColumns.disposition && hasDisposition) row.push(escapeCSV(disposition));
      if (selectedColumns.sentiment && hasSentiment) row.push(escapeCSV(sentiment));
      if (selectedColumns.purpose && hasPurpose) row.push(escapeCSV(purpose));
      if (selectedColumns.linkedin_url) row.push(prospectLinkedInUrl);
      if (selectedColumns.transcript_link) row.push(transcriptLink);
      if (selectedColumns.transcript_text) row.push(escapeCSV(transcriptText));

      return row;
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `session_export_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const escapeCSV = (value: string): string => {
    if (!value) return '';
    // Escape quotes and wrap in quotes if contains comma, quote, or newline
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <Typography variant="h3" color="#1F2937" fontWeight={600}>
            Export Session Data
          </Typography>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
            disabled={progress.isExporting}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Date Range Selection */}
          <div className="space-y-3">
            <div>
              <label className="block mb-2">
                <Typography variant="mediumParagraph" color="#374151" fontWeight={500}>
                  Start Date
                </Typography>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    validateDateRange(e.target.value, endDate);
                  }}
                  disabled={progress.isExporting}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2">
                <Typography variant="mediumParagraph" color="#374151" fontWeight={500}>
                  End Date
                </Typography>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    validateDateRange(startDate, e.target.value);
                  }}
                  disabled={progress.isExporting}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Date Error */}
          {dateError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <Typography variant="smallParagraph" color="#DC2626">
                {dateError}
              </Typography>
            </div>
          )}

          {/* Column Selection */}
          {!progress.isExporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Typography variant="mediumParagraph" color="#374151" fontWeight={600}>
                  Select Columns to Export
                </Typography>
                <button
                  onClick={() => {
                    const allChecked = Object.values(selectedColumns).every(v => v);
                    const newState = Object.keys(selectedColumns).reduce((acc, key) => {
                      acc[key] = !allChecked;
                      return acc;
                    }, {} as Record<string, boolean>);
                    setSelectedColumns(newState);
                  }}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {Object.values(selectedColumns).every(v => v) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border border-gray-200 rounded-md p-3 max-h-60 overflow-y-auto bg-gray-50">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.timestamp}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, timestamp: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Timestamp</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.rep_name}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, rep_name: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Rep Name</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.prospect_name}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, prospect_name: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Prospect Name</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.prospect_title}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, prospect_title: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Prospect Title</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.company_name}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, company_name: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Company Name</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.cadence}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, cadence: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Cadence</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.prospect_list}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, prospect_list: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Prospect List</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.from_number}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, from_number: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">From Number</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.to_number}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, to_number: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">To Number</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.duration}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, duration: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Duration (seconds)</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.parallel_lines}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, parallel_lines: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Parallel Lines</Typography>
                  </label>
                  {hasDisposition && (
                    <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedColumns.disposition}
                        onChange={(e) => setSelectedColumns({ ...selectedColumns, disposition: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                      />
                      <Typography variant="mediumParagraph" color="#374151">Disposition</Typography>
                    </label>
                  )}
                  {hasSentiment && (
                    <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedColumns.sentiment}
                        onChange={(e) => setSelectedColumns({ ...selectedColumns, sentiment: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                      />
                      <Typography variant="mediumParagraph" color="#374151">Sentiment</Typography>
                    </label>
                  )}
                  {hasPurpose && (
                    <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedColumns.purpose}
                        onChange={(e) => setSelectedColumns({ ...selectedColumns, purpose: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                      />
                      <Typography variant="mediumParagraph" color="#374151">Purpose</Typography>
                    </label>
                  )}
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.linkedin_url}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, linkedin_url: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">LinkedIn URL</Typography>
                  </label>
                  <label className="flex items-center gap-2 hover:bg-white p-1.5 rounded cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedColumns.transcript_link}
                      onChange={(e) => setSelectedColumns({ ...selectedColumns, transcript_link: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                    />
                    <Typography variant="mediumParagraph" color="#374151">Transcript Link</Typography>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Transcript Text Option - Separate Section */}
          {!progress.isExporting && (
            <div className="space-y-2">
              <div className="border border-indigo-200 rounded-md p-3 bg-indigo-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedColumns.transcript_text}
                    onChange={(e) => setSelectedColumns({ ...selectedColumns, transcript_text: e.target.checked })}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-200 cursor-pointer mt-0.5"
                  />
                  <div className="flex-1">
                    <Typography variant="mediumParagraph" color="#374151" fontWeight={600}>
                      Include Transcript Text
                    </Typography>
                    <Typography variant="smallParagraph" color="#6B7280">
                      Adds full conversation transcript with speaker labels. This will increase export file size.
                    </Typography>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Export Error */}
          {progress.error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <Typography variant="smallParagraph" color="#DC2626">
                {progress.error}
              </Typography>
            </div>
          )}

          {/* Progress Bar */}
          {progress.isExporting && (
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="relative w-4 h-4">
                      <div className="absolute inset-0 border-2 border-indigo-200 rounded-full"></div>
                      <div className="absolute inset-0 border-2 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                    <Typography variant="smallParagraph" color="#6B7280">
                      Fetching sessions...
                    </Typography>
                  </div>
                  <Typography variant="smallParagraph" color="#4F46E5" fontWeight={600}>
                    {exportedSessions.length} session{exportedSessions.length !== 1 ? 's' : ''} found
                  </Typography>
                </div>
                <div className="relative w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-shimmer"
                    style={{
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 2s linear infinite'
                    }}
                  />
                </div>
              </div>

              {/* Do Not Close Warning */}
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <Typography variant="smallParagraph" color="#D97706" fontWeight={500}>
                  Please do not close this tab while exporting
                </Typography>
              </div>
            </div>
          )}

          <style>{`
            @keyframes shimmer {
              0% {
                background-position: 200% 0;
              }
              100% {
                background-position: -200% 0;
              }
            }
          `}</style>

          {/* Success Message */}
          {!progress.isExporting && exportedSessions.length > 0 && !progress.error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md">
              <Download className="w-4 h-4 text-green-600 flex-shrink-0" />
              <Typography variant="smallParagraph" color="#059669">
                Successfully exported {exportedSessions.length} session{exportedSessions.length !== 1 ? 's' : ''}
              </Typography>
            </div>
          )}

          {/* Info */}
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
            <Typography variant="smallParagraph" color="#2563EB">
              Maximum date range: 1 week. Export includes all visible columns and transcript links.
            </Typography>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          {progress.isExporting ? (
            <button
              onClick={cancelExport}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleExport}
                disabled={!!dateError || !startDate || !endDate}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export to CSV
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
